import { useQuery } from '@tanstack/react-query'
import { LineItemsApi } from '@/services/lineItems/lineItemsApi'
import { AuthService } from '@/services/core/auth'
import type { ObjectAncestors } from '../components/objectCrumbs'

/**
 * The advertiser, order and line item a creative sits under. A creative names
 * its line item and advertiser but not the order between them, so the line item
 * is read to complete the chain — under the same query key the line item page
 * uses, so opening one after the other costs no extra request.
 *
 * `fallback` fills the trail from what the page already has while that request
 * is in flight.
 */
export function useLineItemAncestors(
  lineItemId: string | undefined | null,
  fallback: ObjectAncestors = {}
): ObjectAncestors {
  const { data: lineItem } = useQuery({
    queryKey: ['lineitem', lineItemId],
    queryFn: () => LineItemsApi.getById(lineItemId!),
    enabled: !!lineItemId && AuthService.isAuthenticated(),
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

  return {
    advertiser: {
      id: lineItem?.advertiserId || fallback.advertiser?.id,
      name: lineItem?.advertiserName || fallback.advertiser?.name,
    },
    order: { id: lineItem?.orderId, name: lineItem?.orderName },
    lineItem: {
      id: lineItem?.id || fallback.lineItem?.id,
      name: lineItem?.name || fallback.lineItem?.name,
    },
  }
}
